import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { SoldOutText } from "./menu-item.styles.jsx";
import {
  SquareMenuItemContainer,
  SquareImageContainer,
  SquareContentContainer,
  SquareFooter,
  SquarePriceContainer,
  SquareImageSkeleton,
  SquareBackgroundImage,
  SquareNameContainer,
  SquareDescriptionContainer,
  SquareImagePlaceholder,
  SquareSoldOutOverlay,
} from "./square-menu-item.styles.jsx";
import { useState } from "react";
import { TransitionsModal } from "../menu-item-modal/menu-item-modal.component.jsx";
import { BundleModal } from "../bundle-modal/bundle-modal.component.jsx";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../utils/firebase/firebase.utils";
import { dishSoldOut } from "../../constants.ts";
import { useMenuItemCardTheme } from "./useMenuItemCardTheme.js";
import { isDishPromoUnavailable } from "../../utils/promoCheck";
import { formatDishItemPriceDisplay } from "../../utils/variantPricing";

const SquareMenuItem = ({ product, kitchen }) => {
  const {
    textBody,
    backgroundPrimary,
    cardBackground,
    brandTertiary,
    skeletonBase,
    skeletonHighlight,
  } = useMenuItemCardTheme();

  const [showModal, setShowModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isBundleItem =
    product?.isBundle === true && Boolean(product?.bundleConfig);

  const toggleShowModal = () => {
    logEvent(analytics, "dish_viewed", {
      kitchenName: kitchen?.kitchenName,
    });
    setShowModal((p) => !p);
  };

  const {
    dishName,
    dishDescription,
    dishPrice,
    remoteUri,
    dishStatus,
    stockDetails,
    variants,
  } = product;
  const isDishOutOfStock =
    stockDetails && stockDetails.isEnabled && stockDetails.quantity === 0;
  const soldOut = dishStatus === dishSoldOut || isDishOutOfStock;
  const promoUnavailable = isDishPromoUnavailable(product);
  const isUnavailable = soldOut || promoUnavailable;
  const unavailableLabel = soldOut ? "Sold Out" : "Unavailable";
  const hasImage = Boolean(remoteUri);
  const priceDisplay = formatDishItemPriceDisplay(dishPrice, variants);

  return (
    <>
      <SquareMenuItemContainer
        onClick={toggleShowModal}
        cardBackground={cardBackground}
      >
        <SquareImageContainer>
          {hasImage ? (
            <>
              <SquareImageSkeleton
                hidden={imageLoaded}
                skeletonBase={skeletonBase}
                skeletonHighlight={skeletonHighlight}
              />
              <SquareBackgroundImage
                className="image"
                src={remoteUri}
                alt={dishName}
                loaded={imageLoaded}
                soldOut={isUnavailable}
                loading="lazy"
                decoding="async"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
              />
            </>
          ) : (
            <SquareImagePlaceholder
              placeholderBackground={skeletonBase}
              iconColor={`${textBody}66`}
            >
              <ImageOutlinedIcon sx={{ fontSize: 48, color: "inherit" }} />
            </SquareImagePlaceholder>
          )}
          {isUnavailable && (
            <SquareSoldOutOverlay>
              <SoldOutText
                textBody={textBody}
                backgroundPrimary={backgroundPrimary}
              >
                {unavailableLabel}
              </SoldOutText>
            </SquareSoldOutOverlay>
          )}
        </SquareImageContainer>
        <SquareContentContainer>
          <SquareNameContainer soldOut={isUnavailable} brandTertiary={brandTertiary}>
            {dishName}
          </SquareNameContainer>
          {dishDescription && (
            <SquareDescriptionContainer soldOut={isUnavailable} textBody={textBody}>
              {dishDescription.replaceAll("\\n", "\n")}
            </SquareDescriptionContainer>
          )}
          <SquareFooter>
            <SquarePriceContainer soldOut={isUnavailable} textBody={textBody}>
              {priceDisplay}
            </SquarePriceContainer>
          </SquareFooter>
        </SquareContentContainer>
      </SquareMenuItemContainer>
      {showModal &&
        (isBundleItem ? (
          <BundleModal
            product={product}
            showModal={showModal}
            toggleShowModal={toggleShowModal}
            kitchen={kitchen}
          />
        ) : (
          <TransitionsModal
            product={product}
            showModal={showModal}
            toggleShowModal={toggleShowModal}
            kitchen={kitchen}
          />
        ))}
    </>
  );
};

export default SquareMenuItem;
